import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as QRCode from 'qrcode';
import { TwoFactorAuthService } from '../../../../services/two-factor-auth.service';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { AuthService } from '../../../../services/api/auth.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { User } from '../../../../models/entities/users';

@Component({
  selector: 'two-factor-setup-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './two-factor-setup-card.component.html',
  styleUrl: './two-factor-setup-card.component.scss'
})
export class TwoFactorSetupCardComponent implements OnChanges, OnDestroy {
  @Input() user?: User;
  @Output() mfaChanged = new EventEmitter<void>();

  settingUp: boolean = false;
  disabling: boolean = false;
  submitting: boolean = false;
  qrCodeSrc: string | null = null;
  secret: string | null = null;
  code: string = '';
  disableCode: string = '';
  error: string | null = null;
  disableError: string | null = null;

  currentUserId: number | null = null;
  canManageOthersMfa: boolean = false;

  private readonly subscription = new Subscription();

  constructor(
    private twoFactorAuthService: TwoFactorAuthService,
    private confirmationDialogService: ConfirmationDialogService,
    private alertNotificationService: AlertNotificationService,
    private authService: AuthService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.subscription.add(
      this.authService.getUserInSession().subscribe(sessionUser => {
        this.currentUserId = sessionUser?.userId ?? null;
      })
    );
    this.subscription.add(
      this.mainLayoutService.activeProfile$.subscribe(profile => {
        this.canManageOthersMfa = this.accessControlService.hasFeatureActionAccess(profile, 'GS_USERS', 'mfaManage');
      })
    );
  }

  get isOwnProfile(): boolean {
    return !!this.user && this.user.userId === this.currentUserId;
  }

  get visible(): boolean {
    return this.isOwnProfile || this.canManageOthersMfa;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      this.resetSetupState();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private resetSetupState(): void {
    this.settingUp = false;
    this.disabling = false;
    this.submitting = false;
    this.qrCodeSrc = null;
    this.secret = null;
    this.code = '';
    this.disableCode = '';
    this.error = null;
    this.disableError = null;
  }

  private isSuccessResponse(response: any): boolean {
    return !response?.status || response.status.code === 200;
  }

  private extractEnrollment(response: any): { secret?: string; otpAuthUri?: string } | null {
    return response?.data?.mfaEnrollment ?? response?.mfaEnrollment ?? null;
  }

  startSetup(): void {
    this.submitting = true;
    this.twoFactorAuthService.enroll().subscribe({
      next: async (response: any) => {
        this.submitting = false;

        if (!this.isSuccessResponse(response)) {
          this.alertNotificationService.error(response?.status?.error || 'Unable to start two-factor setup');
          return;
        }

        const enrollment = this.extractEnrollment(response);
        if (!enrollment?.otpAuthUri) {
          this.alertNotificationService.error('Unable to start two-factor setup: missing enrollment data');
          return;
        }

        this.secret = enrollment.secret ?? null;
        this.qrCodeSrc = await QRCode.toDataURL(enrollment.otpAuthUri);
        this.settingUp = true;
        this.code = '';
        this.error = null;
      },
      error: (error: any) => {
        this.submitting = false;
        const errorMessage = error?.error?.error?.message || 'Unable to start two-factor setup';
        this.alertNotificationService.error(errorMessage);
      }
    });
  }

  confirmSetup(): void {
    if (this.code.trim().length !== 6) {
      return;
    }

    this.submitting = true;
    this.twoFactorAuthService.confirm(this.code.trim()).subscribe({
      next: (response: any) => {
        this.submitting = false;
        if (this.isSuccessResponse(response)) {
          this.alertNotificationService.success('Two-factor authentication enabled successfully');
          this.resetSetupState();
          this.mfaChanged.emit();
        } else {
          this.error = response?.status?.error || 'Invalid code. Please try again.';
        }
      },
      error: (error: any) => {
        this.submitting = false;
        this.error = error?.error?.error?.message || 'Invalid code. Please try again.';
      }
    });
  }

  cancelSetup(): void {
    this.resetSetupState();
  }

  requestDisable(): void {
    if (!this.user) {
      return;
    }

    if (this.isOwnProfile) {
      this.disabling = true;
      this.disableCode = '';
      this.disableError = null;
      return;
    }

    this.confirmationDialogService.open(
      `Are you sure you want to remove two-factor authentication for ${this.user.firstName} ${this.user.lastName}?`,
      () => {
        this.twoFactorAuthService.disableForUser(this.user!.userId).subscribe({
          next: (response: any) => {
            if (this.isSuccessResponse(response)) {
              this.alertNotificationService.success('Two-factor authentication disabled');
              this.mfaChanged.emit();
            } else {
              this.alertNotificationService.error(response?.status?.error || 'Unable to disable two-factor authentication');
            }
          },
          error: (error: any) => {
            const errorMessage = error?.error?.error?.message || 'Unable to disable two-factor authentication';
            this.alertNotificationService.error(errorMessage);
          }
        });
      }
    );
  }

  confirmDisable(): void {
    if (this.disableCode.trim().length !== 6) {
      return;
    }

    this.submitting = true;
    this.twoFactorAuthService.disable(this.disableCode.trim()).subscribe({
      next: (response: any) => {
        this.submitting = false;
        if (this.isSuccessResponse(response)) {
          this.alertNotificationService.success('Two-factor authentication disabled');
          this.resetSetupState();
          this.mfaChanged.emit();
        } else {
          this.disableError = response?.status?.error || 'Invalid code. Please try again.';
        }
      },
      error: (error: any) => {
        this.submitting = false;
        this.disableError = error?.error?.error?.message || 'Invalid code. Please try again.';
      }
    });
  }

  cancelDisable(): void {
    this.disabling = false;
    this.disableCode = '';
    this.disableError = null;
  }
}
